import { Routes, RouterModule } from '@angular/router';
import { LoginComponent } from './pages/unsecure/login/login.component';
import { ValidTokenAuthGuard } from './core/guards/valid.token.guard';
import { DashboardComponent } from './pages/secure/home/dashboard/dashboard.component';
import { NgModule } from '@angular/core';
import { PageNotFoundComponent } from './pages/unsecure/page-not-found/page-not-found.component';
import { ConnectionGuard } from './core/guards/connection.guard';
import { SummaryComponent } from './pages/secure/order/summary/summary.component';
import { OrdersConfirmationComponent } from './pages/secure/order/orders-confirmation/orders-confirmation.component';
import { OrdersRejectionComponent } from './pages/secure/order/orders-rejection/orders-rejection.component';
import { RegisterComponent } from './pages/unsecure/register/register.component';
import { AccountDisabledComponent } from './pages/unsecure/account-disabled/account-disabled.component';
import { ChangePasswordComponent } from './pages/secure/home/change-password/change-password.component';
import { ResetPasswordComponent } from './pages/unsecure/reset-password/reset-password.component';
import { NoTokenAuthGuard } from './core/guards/no.token.guard';
import { ValidGuidAuthGuard } from './core/guards/valid.guid.guard';
import { PermissionAuthGuard } from './core/guards/permission.guard';
import { Module } from './shared/objects/auth/module';
import { CarrierProfileComponent } from './pages/secure/admin/carriers/carrier-profile/carrier-profile.component';
import { CarrierProfileGuard } from './pages/secure/admin/carriers/guards/carrier-profile.guard';
import { CarrierProfileDataGuard } from './pages/secure/admin/carriers/guards/carrier-profile-data.guard';
import { HelpComponent } from './pages/unsecure/help/help.component';
import { InternetConnectionComponent } from './pages/secure/home/internet-connection/internet-connection.component';

const routes: Routes = [
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: 'auth', redirectTo: '/login' },
  { path: 'help', component: HelpComponent },
  { path: 'login', component: LoginComponent, canActivate: [NoTokenAuthGuard] },
  {
    path: 'account-disabled',
    component: AccountDisabledComponent,
    canActivate: [NoTokenAuthGuard],
  },
  {
    path: 'login/:action/:guid',
    component: LoginComponent,
    canActivate: [NoTokenAuthGuard],
  },
  {
    path: 'register',
    component: RegisterComponent,
    canActivate: [NoTokenAuthGuard],
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    canActivate: [NoTokenAuthGuard],
  },
  {
    path: 'reset-password/:guid',
    component: ChangePasswordComponent,
    canActivate: [NoTokenAuthGuard, ValidGuidAuthGuard],
  },
  {
    path: 'secure',
    component: InternetConnectionComponent,
    canActivateChild: [ConnectionGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [ValidTokenAuthGuard],
      },
      {
        path: 'freight-exchange',
        loadChildren: () =>
          import('./pages/secure/freight-exchange/freight-exchange.module').then(
            m => m.FreightExchangeModule,
          ),

        data: { allowedPermissions: [Module.FreightExchange] },
      },
      {
        path: 'order-list',
        loadChildren: () =>
          import('./pages/secure/order/order.module').then(m => m.OrderModule),
        data: { allowedPermissions: [Module.Orders] },
      },
      {
        path: 'self-billing',
        loadChildren: () =>
          import('./pages/secure/self-billing/self-billing.module').then(
            m => m.SelfbillingModule,
          ),
        canActivate: [PermissionAuthGuard, ValidTokenAuthGuard],
        data: { allowedPermissions: [Module.Selfbillings] },
      },
      {
        path: 'tender',
        loadChildren: () =>
          import('./pages/secure/tenders/tender.module').then(
            m => m.TenderModule,
          ),
        canActivate: [PermissionAuthGuard, ValidTokenAuthGuard],
        data: { allowedPermissions: [Module.Tenders] },
      },
      {
        path: 'ex-work-timeslot',
        loadChildren: () =>
          import('./pages/secure/ex-work-timeslot/ex-work-orders.module').then(
            m => m.ExWorkOrdersModule,
          ),
        canActivate: [PermissionAuthGuard, ValidTokenAuthGuard],
        data: { allowedPermissions: [Module.ExWorkBooking] },
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./pages/secure/admin/administration.module').then(
            m => m.AdministrationModule,
          ),
        canActivate: [ValidTokenAuthGuard],
      },
      {
        path: 'summary/:result',
        component: SummaryComponent,
        canActivate: [ValidTokenAuthGuard],
      },
      {
        path: 'change-password',
        component: ChangePasswordComponent,
        canActivate: [ValidTokenAuthGuard],
      },
      {
        path: 'instruction-docs',
        loadChildren: () =>
          import('./pages/secure/instruction-docs/instruction-docs.module').then(
            m => m.InstructionDocsModule,
          ),
        canActivate: [ValidTokenAuthGuard],
      },
      {
        path: 'incidents',
        loadChildren: () =>
          import('./pages/secure/incidents/incidents.module').then(
            m => m.IncidentsModule,
          ),
        canActivate: [PermissionAuthGuard, ValidTokenAuthGuard],
        data: { allowedPermissions: [Module.Incidents] },
      },
      {
        path: 'carriers/my-profile',
        component: CarrierProfileComponent,
        canActivate: [CarrierProfileGuard, CarrierProfileDataGuard],
      },
    ],
  },
  {
    path: 'secure/confirm/:guid',
    component: OrdersConfirmationComponent,
  },
  {
    path: 'secure/reject/:guid',
    component: OrdersRejectionComponent,
  },
  { path: '**', redirectTo: '/login', pathMatch: 'full' },
  { path: 'page-not-found', component: PageNotFoundComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
